import type { DocCache } from '@colplat/core-client';
import { apiFetch } from '../api.js';
import { readCached } from '../offlineReads.js';

/**
 * One person's webledger preferences, server-synced — legacy's
 * `prefs`/`pref_set`, which every page there loaded once at startup.
 *
 * These were `localStorage` keys until the `settings` table existed, with a
 * comment on each saying "revisit if cross-device parity matters". It does:
 * "which currency did I last use" is a fact about a person, not about a
 * browser profile, and the localStorage version silently forgot it every time
 * someone opened the ledger on their laptop instead of their desktop.
 *
 * Reads go through the usual cache, so a page opened offline still remembers
 * what you chose. Writes are optimistic and fire-and-forget: the value is
 * live in the UI before the request goes out, and a failed request leaves the
 * choice applied for this session rather than snapping a control back to its
 * old value while you are looking at it. The cost of losing one is that a
 * preference reverts on the next reload, which is the right price for never
 * blocking on one.
 */

const CACHE_KEY = 'prefs';

export interface PrefsSetOptions {
  /** Let the request outlive the page — for a write made on the way out. */
  keepalive?: boolean;
}

export interface PrefsHandle {
  get(key: string): string | undefined;
  /** Applies immediately, then syncs. Never throws and never awaits on the caller's behalf. */
  set(key: string, value: string, opts?: PrefsSetOptions): void;
}

export async function loadPrefs(docCache: DocCache, token: string): Promise<PrefsHandle> {
  const read = await readCached<{ prefs: Record<string, string | null> }>(
    docCache, CACHE_KEY, '/apps/webledger/prefs', token,
  );
  // A page whose preferences could not be loaded still works, with defaults —
  // legacy degrades the same way, and there is nothing here worth failing a
  // page load over.
  const values = new Map<string, string>();
  if (read.ok) {
    for (const [key, value] of Object.entries(read.data.prefs)) {
      if (value !== null) values.set(key, value);
    }
  }

  return {
    get(key) { return values.get(key); },
    set(key, value, opts) {
      if (values.get(key) === value) return;   // no request for a no-op
      values.set(key, value);
      // Written back to the cache too, so a preference chosen offline is
      // still there after a reload rather than only until this page closes.
      void docCache.set(CACHE_KEY, { prefs: Object.fromEntries(values) });
      void apiFetch('/apps/webledger/prefs', {
        method: 'PUT', token, body: { key, value }, ...(opts?.keepalive ? { keepalive: true } : {}),
      }).catch(() => undefined);
    },
  };
}

/**
 * A preference that holds a list of record ids — the two collapse states, and
 * the picker's recent list.
 *
 * JSON both ways. The chart of accounts used to store a comma-joined string
 * and the picker a JSON array, for no better reason than that they were
 * written months apart; one shape means one pair of helpers and no thinking
 * about which is which. Reading still accepts the old comma form, because a
 * preference that silently forgets a 59-account chart's folder state on the
 * day of the change is a worse trade than four lines of tolerance.
 */
export function parseIdList(raw: string | undefined): string[] {
  if (!raw) return [];
  const text = raw.trim();
  if (!text.startsWith('[')) return text.split(',').filter(Boolean);   // the old format
  try {
    const value: unknown = JSON.parse(text);
    return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    // A malformed preference should cost the reader a default, never an error.
    return [];
  }
}

export function serializeIdList(ids: Iterable<string>): string {
  return JSON.stringify([...ids]);
}

/**
 * The keys webledger uses. Here rather than inline at each call site so the
 * set is greppable — an app's preference keys are a vocabulary, and a typo in
 * one is a silently ignored setting rather than an error.
 */
export const PREF = {
  reportTotalMode: 'report_total_mode',
  /**
   * The two report filters legacy persists, under legacy's own key names so
   * an imported `settings` table carries straight over.
   *
   * Currency and purpose only, matching legacy. The date range deliberately
   * does not persist: a range that silently survives a month is a trap —
   * coming back to a report that hides everything recent looks like missing
   * data, not like a remembered filter. Nor does the folder scope, for the
   * same reason at a larger scale.
   */
  reportCurrency: 'report_all_currency',
  reportPurpose: 'report_all_purpose',
  /**
   * The report's tag selection and its any/all mode.
   *
   * Persisted for the same reason currency and purpose are — a filter you
   * chose is a fact about how you read this ledger, not about this page load.
   * Not under a legacy key, because legacy has no tags.
   */
  reportTags: 'report_all_tags',
  reportTagMode: 'report_all_tag_mode',
  /** The chip tally's selection (JSON, with its own timestamp for the TTL) and its currency mode. */
  tally: 'chip_tally',
  tallyMode: 'chip_tally_mode',
  lastTxCurrency: 'last_tx_currency',
  lastTxDebitAccount: 'last_tx_debit_account',
  lastTxCreditAccount: 'last_tx_credit_account',
  accountsView: 'accounts_view',
  accountsCollapsed: 'accounts_collapsed',
  accountsTotalMode: 'accounts_total_mode',
  /** Whether the chart of accounts shows each account's balance over time. `'1'` when it does. */
  accountsChart: 'accounts_chart',
  /** Wallet balances as they would stand with every debt between wallets settled. `'1'` when on. */
  walletsSettled: 'wallets_settled',
  /** Whether the short form's assistants (accounts, wallets) are open. Collapsed until opened. */
  quickAssist: 'quick_assist',
  /** The account whose register you were last in, so the tree can point at it on return. */
  lastAccount: 'last_account',
  registerShowSplits: 'register_show_splits',
  /** The register's column order and relative widths, as JSON. Legacy keeps this per device; see webledgerRegisterColumns.ts. */
  registerColumns: 'register_columns',
  /**
   * Where you were scrolled to on each list, so returning to one puts you
   * back where you were rather than at the top — legacy's `scroll:*` keys.
   * The register's is per account, because "where I was" in a 400-row
   * register means nothing in a different account's.
   */
  /**
   * Which section of the wallets page you were in, and which wallet's log.
   *
   * The page shows one section at a time (ADR-0023), so without these a
   * reload drops you back on the wallet list every time — and the log is
   * where the work happens. Same reasoning as `lastAccount`: it is a fact
   * about how this person reads the ledger, not about this page load.
   */
  /**
   * The currency the wallet log is narrowed to, '' for any.
   *
   * Remembered for the same reason the tag selection is: a filter you chose
   * is a fact about how you read this ledger, not about this page load. It
   * falls back to any whenever the log on screen does not hold it, so a
   * remembered currency can never show an empty page.
   */
  walletsLogCurrency: 'wallets_log_currency',
  /**
   * The account the log is narrowed to, by id.
   *
   * Like the currency, and unlike the wallet, this is dropped whenever the
   * log on screen does not hold it — an account chosen while looking at
   * Renovation must not leave Car showing nothing with no visible reason.
   */
  walletsLogAccount: 'wallets_log_account',
  walletsSection: 'wallets_section',
  /** Show the cash total by currency, or converted into one — its own key, as every page's is. */
  walletsTotalMode: 'wallets_total_mode',
  /** The wallet log's tag filter and its any/all mode — the reports' pair, per page. */
  walletsTags: 'wallets_tags',
  walletsTagMode: 'wallets_tag_mode',
  walletsLog: 'wallets_log',
  /** The tree picker's folder state and its "Recent" list, both JSON arrays of ids. */
  pickerCollapsed: 'acct_picker_collapsed',
  recentAccounts: 'acct_recent',
  scrollAccounts: 'scroll:accounts',
  scrollReports: 'scroll:reports:all_transactions',
  scrollRegister: (accountId: string): string => `scroll:register:${accountId}`,
  /**
   * The wallet log's position, per wallet, for the same reason the register's
   * is per account: "where I was" in one wallet's log means nothing in
   * another's. The log had none at all, which on the page where most of the
   * work happens meant every return started at the top.
   */
  scrollWalletLog: (walletId: string): string => `scroll:wallet-log:${walletId}`,
} as const;
